import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { I18n } from '../../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../../base/package/util/a11y';
import { getDeviceOrientation } from '../../../base/package/util/device';
import { dispatchCustomEvent } from '../../../base/package/util/event';
import { openDeepLinkInGamification } from '../../../mod-incentive/helper/gamification-launcher';
import type { Reward } from '../../../mod-incentive/types';
import { ArrowDirection } from '../../ui-arrow-button/ui-arrow-button';
import styles from './mission-bar.css?inline';
import '../../ui-icons/ui-chevron-arrow-icon';
import '../../ui-progress-bar/ui-progress-bar';

const CName = 'incentive-mission-bar';

@customElement(CName)
export class IncentiveMissionBar extends LitElement {
  static readonly styles = unsafeCSS(styles);
  @property({ type: String }) bundleId = '';
  @property({ type: Number }) progress = 0;
  @property({ type: Number }) remaining = 0;
  @property({ type: Date }) expirationDate: Date;
  @property({ type: Boolean }) isExpired = false;
  @property({ type: Object }) reward: Reward;
  @property({ type: Boolean }) isDesktop = false;

  @state() private _layout: 'portrait' | 'landscape' = getDeviceOrientation();
  @state() private _hasActiveNotification = false;
  @state() private _notification?: { text: string } | null;

  @query('.mission-bar__notification-wrapper') private _notificationWrapperEl?: HTMLElement;
  @query('.mission-bar__notification') private _notificationEl?: HTMLElement;

  protected updated(changed: Map<string, unknown>) {
    if (!changed.has('remaining')) return;

    const prevRemaining = (changed.get('remaining') as number | undefined) ?? this.remaining;

    if (prevRemaining !== 0 && this.remaining === 0) {
      this._notification = { text: window.$app.$t.get(I18n.incentive.missionBar.missionCompleted) };
      this._startNotification();
    }
  }

  render() {
    const missionBarClasses = classMap({
      'mission-bar': true,
      'mission-bar--desktop': this.isDesktop,
      'mission-bar--complete': this.remaining === 0,
      'mission-bar--expired': this.isExpired,
      'mission-bar--notification': this._hasActiveNotification,
      'mission-bar--simple-notification': this.isDesktop || this._layout === 'landscape',
    });

    return html`
      <div class=${missionBarClasses} @click=${this._navigateToBundle} tabindex="1"
        @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._navigateToBundle.bind(this))}
      >
        ${this.remaining === 0 ? this._renderComplete() : this._renderInProgress()}
        ${this._renderNotification()}
      </div>
    `;
  }

  private _renderInProgress() {
    return html`
      <ui-progress-bar
        class="mission-bar__progress-bar"
        .progress=${this.progress}
        .remainingAmount=${this.remaining}
        .isIncentiveGlow=${this.progress > 0}
        .showText=${false}
        .isDisabled=${this.isExpired}
      ></ui-progress-bar>
      <div class="mission-bar__end">
        ${this._renderChevron()}
      </div>
    `;
  }

  private _renderComplete() {
    return html`
    ${window.$app.$t.get(I18n.incentive.missionBar.missionCompleted)}
    <div class="mission-bar__reward" @animationend=${this._onMissionComplete}>
      ${this.reward.formattedValue} ${window.$app.$t.get(I18n.incentive.missionBar.win)}
    </div>
  `;
  }

  private _renderChevron() {
    return html`
      <ui-chevron-arrow-icon
        class="mission-bar__chevron"
        .arrowDirection=${ArrowDirection.UP}
        tabindex="-1"
      ></ui-chevron-arrow-icon>
    `;
  }

  private _renderNotification() {
    if (!this._hasActiveNotification || !this._notification?.text) return null;

    return html`
      <div class="mission-bar__notification-wrapper">
        <div class="mission-bar__notification" @animationend=${this._onNotificationAnimationEnd}>
          ${this._notification.text}
        </div>
      </div>
    `;
  }

  private async _startNotification() {
    this._hasActiveNotification = true;

    await this.updateComplete;

    const wrapper = this._notificationWrapperEl;
    const notification = this._notificationEl;
    if (!wrapper || !notification) return;

    wrapper.classList.remove('mission-bar__notification-wrapper--animating');
    notification.classList.remove('mission-bar__notification--animating');

    // force reflow to restart animation (intentional void access)
    void notification.offsetWidth; // NOSONAR — deliberate reflow trigger to reset CSS animation

    wrapper.classList.add('mission-bar__notification-wrapper--animating');
    notification.classList.add('mission-bar__notification--animating');
  }

  private _onNotificationAnimationEnd(e: AnimationEvent) {
    if (e.animationName !== 'notification-puff' && e.animationName !== 'notification-fade') return;
    this._hasActiveNotification = false;
  }

  private _onMissionComplete() {
    dispatchCustomEvent(this, 'missionComplete', {
      bubbles: true,
      composed: true,
    });
  }

  private _navigateToBundle() {
    openDeepLinkInGamification('mission', this.bundleId);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: IncentiveMissionBar;
  }
}
